import { useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";
import { useShallow } from "zustand/shallow";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import { Box, BoxProps, Button, Tab, Tabs, Typography } from "@mui/material";
import { mdiDoorOpen, mdiEmoticonSadOutline } from "@mdi/js";
import Icon from "@mdi/react";

import AppBar from "components/AppBar";
import ConfirmDialog from "components/ConfirmDialog";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import PageLayout from "components/PageLayout";
import PageLoader from "components/PageLoader";
import CircularLoader from "components/CircularLoader/CircularLoader";
import useNotifier from "hooks/useNotifier";
import useBoundStore from "store";
import { ROOM_MANAGER_TAB } from "store/selectedSlice";
import EventAppsApi from "api/event-apps";
import RoomManagerApi, { RoomManagerData, EMPTY_ROOM_MANAGER_DATA } from "api/room-manager";
import { VoteType } from "api/vote";
import { hasGroupRole } from "utils/groupRoles";
import Setup from "./Setup";
import Rooms from "./Rooms";

interface TabPanelProps extends BoxProps {
  "aria-labelledby": string;
  children?: React.ReactNode;
  id: string;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...rest } = props;
  return (
    <Box role='tabpanel' p={2} hidden={value !== index} {...rest}>
      {value === index && children}
    </Box>
  );
}

export interface RoomManagerProps {
  onBack?: () => void;
}

export default function RoomManager(props: RoomManagerProps) {
  const { onBack } = props;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isLoadingEventApps, setIsLoadingEventApps] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isActivating, setIsActivating] = useState(false);
  const [roomManagerData, setRoomManagerData] = useState<RoomManagerData | null>(null);

  const isAppFocused = useBoundStore((state) => state.miscellaneous.isAppFocused);
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const selected = useBoundStore((state) => state.selected);
  const userAuthId = useBoundStore((state) => state.user.auth?.id);
  const { events, groups, groupsUsers, votes } = useBoundStore(
    useShallow((state) => ({
      events: state.appData.events,
      groups: state.appData.groups,
      groupsUsers: state.appData.groupsUsers,
      votes: state.appData.votes,
    }))
  );

  const event = events.find((e) => e.id === selected.eventId);
  const group = groups.find((g) => g.id === selected.groupId);
  const isAdmin =
    Boolean(group?.admins.find((id) => id === userAuthId)) || hasGroupRole(group, userAuthId!, "manage-rooms");

  const eventRef = useRef(event);
  const groupRef = useRef(group);
  useEffect(() => {
    eventRef.current = event;
  }, [event]);
  useEffect(() => {
    groupRef.current = group;
  }, [group]);

  // Participants = group members who voted "participating" on this event
  const participantIds = votes
    .filter((v) => v.event_id === selected.eventId && v.type === VoteType.PARTICIPATING)
    .map((v) => v.uid);
  const participants = groupsUsers.filter((u) => participantIds.includes(u.uid));

  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { showNotification } = useNotifier();

  const activeRoomManager = selected.eventApps?.includes("room-manager");
  const eventAppsApi = useMemo(() => new EventAppsApi(showNotification), [showNotification]);
  const roomManagerApi = useMemo(() => new RoomManagerApi(showNotification), [showNotification]);

  useEffect(() => {
    if (navigationType === "POP") {
      setConfirmDialogOpen(false);
    }
  }, [navigationType]);

  /**
   * Fetch active apps on initial load when navigating directly via URL.
   */
  useEffect(() => {
    (async () => {
      if (isAppFocused && event && group) {
        if (selected.eventApps === null) {
          setIsLoadingEventApps(true);
          const { data } = await eventAppsApi.getActiveApps(event.id, group.id);
          if (data) {
            selected.set({ eventApps: data.active_apps });
          }
        }
        setIsLoadingEventApps(false);
      }
    })();
  }, [event, group, isAppFocused, selected.eventApps]);

  /**
   * Fetch room manager data when the app is confirmed active.
   */
  useEffect(() => {
    (async () => {
      if (!isAppFocused) return;

      if (activeRoomManager && event && group) {
        const { data } = await roomManagerApi.getRoomManagerData(event.id, group.id);
        setRoomManagerData(data?.data ?? EMPTY_ROOM_MANAGER_DATA);
        setIsLoadingData(false);
      } else {
        setIsLoadingData(false);
      }
    })();
  }, [isAppFocused, activeRoomManager, event?.id, group?.id]);

  const debouncedSave = useMemo(
    () =>
      debounce(async (data: RoomManagerData) => {
        const ev = eventRef.current;
        const grp = groupRef.current;
        if (!ev || !grp) return;
        await roomManagerApi.saveRoomManagerData(ev.id, grp.id, data);
      }, 800),
    [roomManagerApi]
  );

  function handleSave(updatedData: RoomManagerData) {
    if (!event || !group) return;
    setRoomManagerData(updatedData);
    debouncedSave(updatedData);
  }

  async function handleActivate() {
    if (!event || !group) return;
    setIsActivating(true);
    const { error } = await roomManagerApi.activateRoomManager(event.id, group.id);
    setIsActivating(false);
    if (!error) {
      selected.set({ eventApps: [...(selected.eventApps ?? []), "room-manager"] });
      showNotification({ message: "Room Manager successfully activated.", severity: "success" });
    }
  }

  function handleOpenConfirmDialog() {
    setConfirmDialogOpen(true);
    navigate(location.pathname);
  }

  function handleCloseConfirmDialog() {
    setConfirmDialogOpen(false);
    navigate(-1);
  }

  async function handleConfirmCancel() {
    if (!event || !group) return;
    const { error } = await roomManagerApi.cancelRoomManager(event.id, group.id);
    navigate(-1);
    if (!error) {
      selected.set({ eventApps: selected.eventApps?.filter((app) => app !== "room-manager") });
    }
  }

  function handleTabChange(_event: React.SyntheticEvent, newValue: number) {
    selected.set({ roomManagerTab: newValue });
  }

  function renderAppBar(renderTabs = false): JSX.Element {
    const actions: JSX.Element[] = [];
    if (activeRoomManager && isAdmin) {
      actions.push(
        <Button color='inherit' key='delete' onClick={handleOpenConfirmDialog}>
          Delete
        </Button>
      );
    }

    return (
      <>
        <AppBar actions={actions} title='Room Manager' onBack={onBack} />
        {renderTabs && isAdmin && (
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              variant={isMobileDevice ? "fullWidth" : undefined}
              value={selected.roomManagerTab}
              onChange={handleTabChange}
              aria-label='Room Manager tabs'
            >
              <Tab id='rm-setup-tab' aria-controls='rm-setup-tabpanel' label='Setup' />
              <Tab id='rm-rooms-tab' aria-controls='rm-rooms-tabpanel' label='Rooms' />
            </Tabs>
          </Box>
        )}
      </>
    );
  }

  if (!event || !group) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder icon={mdiEmoticonSadOutline} text='Something went wrong' />
      </PageLayout>
    );
  }

  if (isLoadingEventApps || isLoadingData) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <PageLoader />
      </PageLayout>
    );
  }

  if (!activeRoomManager) {
    return isAdmin ? (
      <PageLayout appBar={renderAppBar()}>
        <Box
          alignItems='center'
          display='flex'
          flexDirection='column'
          p={2}
          position='absolute'
          pt={4}
          sx={{ transform: "translateY(-50%)" }}
          top='50%'
          width='100%'
        >
          <Icon path={mdiDoorOpen} size={2.5} />
          <Typography align='center' gutterBottom sx={{ mt: 2 }}>
            Assign participants to rooms for this event.
          </Typography>

          <Box mt={5}>
            <CircularLoader loading={isActivating}>
              <Button
                disabled={isActivating}
                size='large'
                startIcon={<Icon path={mdiDoorOpen} size={1} />}
                variant='contained'
                onClick={handleActivate}
              >
                Activate Room Manager
              </Button>
            </CircularLoader>
          </Box>
        </Box>
      </PageLayout>
    ) : (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder icon={mdiDoorOpen} text='Room Manager can be activated only by group administrators' />
      </PageLayout>
    );
  }

  return (
    <>
      <PageLayout
        appBar={renderAppBar(true)}
        minusHeight={isAdmin ? "7rem" : "3.5rem"}
        paddingBottom={false}
        containerSx={{ position: "static" }}
      >
        {isAdmin ? (
          <>
            <TabPanel
              aria-labelledby='rm-setup-tab'
              id='rm-setup-tabpanel'
              index={ROOM_MANAGER_TAB.setup}
              value={selected.roomManagerTab}
            >
              <Setup data={roomManagerData ?? EMPTY_ROOM_MANAGER_DATA} onSave={handleSave} />
            </TabPanel>
            <TabPanel
              aria-labelledby='rm-rooms-tab'
              id='rm-rooms-tabpanel'
              index={ROOM_MANAGER_TAB.rooms}
              value={selected.roomManagerTab}
              p={0}
            >
              <Rooms
                data={roomManagerData ?? EMPTY_ROOM_MANAGER_DATA}
                isAdmin={isAdmin}
                participants={participants}
                onSave={handleSave}
              />
            </TabPanel>
          </>
        ) : (
          <Rooms
            data={roomManagerData ?? EMPTY_ROOM_MANAGER_DATA}
            isAdmin={false}
            participants={participants}
            onSave={handleSave}
          />
        )}
      </PageLayout>

      <ConfirmDialog
        body='All room and zone data will be permanently deleted.'
        open={confirmDialogOpen}
        title='Delete Room Manager?'
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmCancel}
      />
    </>
  );
}
